import { Group, ActionIcon, Box, Pagination, Select, Autocomplete, Tooltip } from '@mantine/core'
import { TbFilter, TbDownload, TbSortAscending, TbSearch, TbSortDescending } from 'react-icons/tb'
import { ConfigureView } from '../ConfigureView'
import { TableActionsProps, TableActions as TableActionsEnum } from './types'
import { BiSort } from 'react-icons/bi'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { TFunction } from 'i18next'

type ActionPosition = 'left' | 'right'

function renderActions<T>(
  restrict: TableActionsEnum[] | undefined,
  props: TableActionsProps<T>,
  type: ActionPosition,
  t: TFunction
) {
  const {
    columns,
    onColumnsChange,
    configTitle,
    onDownloadCsv,
    data,
    filterableColumns,
    sortDirection,
    onSortDirectionChange,
    sortField,
    onSortFieldChange,
    onFilterFieldChange,
    onSearchChange,
    search,
    filterField,
    getFilterOptions,
    optionsFilter,
    filteredData,
    itemsPerPage,
    currentPage,
    onPageChange,
    csvFileName,
  } = props

  const tableControlInputStyles = {
    color: 'light-dark(var(--mantine-color-text), #e9ecef)',
    '--input-placeholder-color': 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
    '::placeholder': {
      color: 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
      opacity: 1,
    },
    '&:disabled, &[data-disabled]': {
      color: 'light-dark(var(--mantine-color-gray-7), #c9c9c9)',
      WebkitTextFillColor: 'light-dark(var(--mantine-color-gray-7), #c9c9c9)',
      opacity: 1,
    },
    '&:disabled::placeholder, &[data-disabled]::placeholder': {
      color: 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
      opacity: 1,
    },
  }

  const actions = []

  if (type === 'left') {
    // Configure action
    if (!restrict?.includes(TableActionsEnum.Configure)) {
      actions.push(
        <ConfigureView key="configure" columns={columns} onColumnsChange={onColumnsChange} title={configTitle} />
      )
    }

    // Download CSV action
    if (!restrict?.includes(TableActionsEnum.DownloadCsv)) {
      actions.push(
        <Tooltip
          key="download"
          label={t('downloadCsv', { fileName: csvFileName })}
          arrowOffset={10}
          position="top"
          withArrow
        >
          <ActionIcon variant="default" onClick={onDownloadCsv} disabled={data.length === 0} size={36}>
            <TbDownload size={20} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />
          </ActionIcon>
        </Tooltip>
      )
    }

    // Sort actions
    if (!restrict?.includes(TableActionsEnum.Sort)) {
      actions.push(
        <Select
          key="sort"
          disabled={data.length === 0}
          placeholder={t('sortBy')}
          data={filterableColumns}
          clearable
          leftSection={<BiSort size={16} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />}
          maw={200}
          styles={{
            input: {
              color: 'light-dark(var(--mantine-color-text), #e9ecef)',
            },
            section: {
              color: 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
            },
          }}
          value={sortField as string}
          onChange={value => {
            if (value) {
              onSortFieldChange?.(value as keyof T)
            }
          }}
        />
      )
    }

    if (!restrict?.includes(TableActionsEnum.SortDirectionChange) && sortDirection) {
      actions.push(
        <Tooltip
          key="sort-direction"
          label={sortDirection === 'asc' ? t('sortZa') : t('sortAz')}
          arrowOffset={10}
          position="top"
          withArrow
        >
          <ActionIcon
            variant="default"
            onClick={() => {
              onSortDirectionChange?.(sortDirection === 'asc' ? 'desc' : 'asc')
            }}
            size={36}
          >
            {sortDirection === 'asc' ? (
              <TbSortAscending size={20} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />
            ) : (
              <TbSortDescending size={20} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />
            )}
          </ActionIcon>
        </Tooltip>
      )
    }
  } else {
    // Filter action
    if (!restrict?.includes(TableActionsEnum.Filter)) {
      actions.push(
        <Box key="filter-search" style={{ display: 'flex', gap: '8px' }}>
          <Select
            data={filterableColumns}
            leftSection={<TbFilter size={16} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />}
            placeholder={t('filterBy')}
            onChange={value => {
              if (value) {
                onFilterFieldChange?.(value as keyof T)
              }
            }}
            clearable
            onClear={() => {
              onFilterFieldChange?.(undefined)
              onSearchChange?.('')
            }}
            maw={200}
            styles={{
              input: tableControlInputStyles,
              section: {
                color: 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
              },
            }}
          />
          <Autocomplete
            leftSection={<TbSearch size={16} color="light-dark(var(--mantine-color-gray-6), #c9c9c9)" />}
            data={getFilterOptions()}
            placeholder={t('search')}
            value={search || ''}
            onChange={onSearchChange}
            disabled={!filterField}
            filter={optionsFilter}
            styles={{
              input: tableControlInputStyles,
              section: {
                color: 'light-dark(var(--mantine-color-gray-6), #c9c9c9)',
              },
            }}
          />
        </Box>
      )
    }

    // Pagination action
    if (!restrict?.includes(TableActionsEnum.Paginate) && filteredData && filteredData.length > itemsPerPage) {
      actions.push(
        <Pagination
          key="pagination"
          total={Math.ceil(filteredData.length / itemsPerPage)}
          value={currentPage}
          onChange={onPageChange}
        />
      )
    }
  }

  return actions
}

export function TableActions<T>(props: TableActionsProps<T>) {
  const { hideConfig = false, restrict } = props
  const { t } = useTranslation('common')

  const leftActions = useMemo(() => renderActions(restrict, props, 'left', t), [restrict, props, t])
  const rightActions = useMemo(() => renderActions(restrict, props, 'right', t), [restrict, props, t])

  if (hideConfig) {
    return null
  }

  return (
    <Group justify="space-between">
      <Box style={{ display: 'flex', alignItems: 'center', gap: 8 }}>{leftActions}</Box>
      <Group gap="xs">{rightActions}</Group>
    </Group>
  )
}
